import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { FinancialFlowsContextType, FilterModel, initialFinancialFlowsState, initialFilters } from './types'
import { 
  ReportTypeItem, 
  EnumReportOnlineCheckListItem,
  fetchReportTypes, 
  fetchEnumReportOnlineCheckList,
} from 'src/admin/core/services/financial-flows'
import { getAppBarState } from 'src/store'

const FinancialFlowsContext = createContext<FinancialFlowsContextType | undefined>(undefined)

interface FinancialFlowsProviderProps {
  children: React.ReactNode
}

export const FinancialFlowsProvider: React.FC<FinancialFlowsProviderProps> = ({ children }) => {
  // Report Types state
  const [reportTypes, setReportTypes] = useState<ReportTypeItem[]>(initialFinancialFlowsState.reportTypes)
  const [selectedReportType, setSelectedReportType] = useState<ReportTypeItem | null>(
    initialFinancialFlowsState.selectedReportType,
  )
  const [reportTypesLoading, setReportTypesLoading] = useState<boolean>(initialFinancialFlowsState.reportTypesLoading)
  const [reportTypesError, setReportTypesError] = useState<string | null>(initialFinancialFlowsState.reportTypesError)
  
  // Filters state
  const [filters, setFilters] = useState<FilterModel>(initialFilters)
  const [filtersLoading, setFiltersLoading] = useState<boolean>(false)

  // Report Data state
  const [reportData, setReportData] = useState<EnumReportOnlineCheckListItem[]>(initialFinancialFlowsState.reportData)
  const [reportDataLoading, setReportDataLoading] = useState<boolean>(initialFinancialFlowsState.reportDataLoading)
  const [reportDataError, setReportDataError] = useState<string | null>(initialFinancialFlowsState.reportDataError)
  
  const { user } = getAppBarState().appState

  /**
   * Load report types from API
   */
  const loadReportTypes = useCallback(async () => {
    setReportTypesLoading(true)
    setReportTypesError(null)
    const adminUserId = user?.profile?.sub || ''

    try {
      const response = await fetchReportTypes(adminUserId)

      setReportTypes(response.data)

      // Auto-select first report type if none selected
      if (response.data.length > 0 && !selectedReportType) {
        setSelectedReportType(response.data[0])
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al cargar los tipos de reporte'
      setReportTypesError(errorMessage)
      setReportTypes([])
    } finally {
      setReportTypesLoading(false)
    }
  }, [selectedReportType])

  /**
   * Search report data from API using current filters
   */
  const searchReportData = useCallback(async () => {
    if (!selectedReportType) {
      setReportDataError('Debe seleccionar un tipo de reporte')
      return
    }

    setReportDataLoading(true)
    setReportDataError(null)
    setReportData([])

    const adminUserId = user?.profile?.sub || ''

    // Determine entity ID from filters (product or agency/location)
    let entityId = 0
    if (selectedReportType.ProductRequired && filters.product) {
      // For product-based reports, use the product ID from filters
      entityId = parseInt(filters.product, 10) || 0
    }
    if (selectedReportType.AgencyRequired && filters.agencyId) {
      // For agency/location-based reports, use agencyId from filters
      entityId = parseInt(filters.agencyId, 10) || 0
    }

    try {
      // Remove the 'Z' from dates if present (API expects format without timezone indicator)
      const initialDate = filters.from.replace('Z', '')
      const finalDate = filters.to.replace('Z', '')

      const response = await fetchEnumReportOnlineCheckList({
        adminUserId,
        enumName: selectedReportType.Name,
        initialDate,
        finalDate,
        entityId,
      })

      setReportData(response.data)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al buscar los datos del reporte'
      setReportDataError(errorMessage)
      setReportData([])
    } finally {
      setReportDataLoading(false)
    }
  }, [filters, selectedReportType, user?.profile?.sub])

  const contextValue = useMemo<FinancialFlowsContextType>(
    () => ({
      // State
      reportTypes,
      selectedReportType,
      reportTypesLoading,
      reportTypesError,
      filters,
      filtersLoading,
      reportData,
      reportDataLoading,
      reportDataError,
      // Actions
      setReportTypes,
      setSelectedReportType,
      setReportTypesLoading,
      setReportTypesError,
      loadReportTypes,
      setFilters,
      setFiltersLoading,
      setReportData,
      setReportDataLoading,
      setReportDataError,
      searchReportData,
    }),
    [
      reportTypes, 
      selectedReportType, 
      reportTypesLoading, 
      reportTypesError, 
      filters, 
      filtersLoading,
      reportData,
      reportDataLoading,
      reportDataError, 
      loadReportTypes,
      searchReportData,
    ],
  )

  return <FinancialFlowsContext.Provider value={contextValue}>{children}</FinancialFlowsContext.Provider>
}

/**
 * Hook to use the Financial Flows context
 */
export const useFinancialFlows = (): FinancialFlowsContextType => {
  const context = useContext(FinancialFlowsContext)

  if (!context) {
    throw new Error('useFinancialFlows must be used within a FinancialFlowsProvider')
  }

  return context
}

